import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, User, Calendar, Mail, Shield, Crown, Save, CreditCard, AlertTriangle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ANIMALS = [
    { value: 0, label: 'Rat', chinese: '鼠' },
    { value: 1, label: 'Ox', chinese: '牛' },
    { value: 2, label: 'Tiger', chinese: '虎' },
    { value: 3, label: 'Rabbit', chinese: '兔' },
    { value: 4, label: 'Dragon', chinese: '龍' },
    { value: 5, label: 'Snake', chinese: '蛇' },
    { value: 6, label: 'Horse', chinese: '馬' },
    { value: 7, label: 'Goat', chinese: '羊' },
    { value: 8, label: 'Monkey', chinese: '猴' },
    { value: 9, label: 'Rooster', chinese: '雞' },
    { value: 10, label: 'Dog', chinese: '狗' },
    { value: 11, label: 'Pig', chinese: '豬' }
];

function Settings() {
    const { user, token } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState(null);
    const [hasProAccess, setHasProAccess] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [subscription, setSubscription] = useState(null);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);

    // Form state
    const [birthYear, setBirthYear] = useState('');
    const [birthMonth, setBirthMonth] = useState('');
    const [birthDay, setBirthDay] = useState('');
    const [birthHour, setBirthHour] = useState('');
    const [birthMinute, setBirthMinute] = useState('');
    const [birthDataSaveCount, setBirthDataSaveCount] = useState(0);
    const birthDataLocked = birthDataSaveCount >= 2;

    useEffect(() => {
        const fetchProfile = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                // Fetch user profile
                const profileRes = await axios.get(`${API_URL}/api/user/profile`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProfile(profileRes.data);
                setBirthYear(profileRes.data.birth_year || '');
                setBirthMonth(profileRes.data.birth_month || '');
                setBirthDay(profileRes.data.birth_day || '');
                setBirthHour(profileRes.data.birth_hour || '');
                setBirthMinute(profileRes.data.birth_minute || '');
                setBirthDataSaveCount(profileRes.data.birth_data_save_count || 0);
                
                // Set admin/pro from profile data
                setIsAdmin(profileRes.data.is_admin || false);
                setHasProAccess(profileRes.data.has_pro_access || false);
                
                // Fetch subscription status
                try {
                    const subRes = await axios.get(`${API_URL}/api/subscription/status`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setSubscription(subRes.data);
                } catch (subError) {
                    console.log('No subscription found');
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
                toast.error('Failed to load profile');
            }
            setLoading(false);
        };

        fetchProfile();
    }, [token]);

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? You will keep PRO access until the end of your billing period.')) {
            return;
        }
        
        setCancellingSubscription(true);
        try {
            const response = await axios.post(
                `${API_URL}/api/subscription/cancel`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Subscription cancelled. You will keep PRO access until ' + new Date(response.data.access_until).toLocaleDateString());
            // Refresh subscription status
            const subRes = await axios.get(`${API_URL}/api/subscription/status`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSubscription(subRes.data);
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
        }
        setCancellingSubscription(false);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await axios.put(
                `${API_URL}/api/user/profile`,
                {
                    birth_year: birthYear ? parseInt(birthYear) : null,
                    birth_month: birthMonth ? parseInt(birthMonth) : null,
                    birth_day: birthDay ? parseInt(birthDay) : null,
                    birth_hour: birthHour ? parseInt(birthHour) : null,
                    birth_minute: birthMinute ? parseInt(birthMinute) : null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Profile updated successfully');
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Failed to update profile');
        }
        setSaving(false);
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Card className="max-w-md">
                    <CardContent className="pt-6 text-center">
                        <p className="text-muted-foreground">Please login to view settings.</p>
                        <Button className="mt-4" onClick={() => window.location.href = '/login'}>
                            Login
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center" data-testid="settings-loading">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const currentAnimal = ANIMALS.find(a => a.value === profile?.birth_animal_index);

    return (
        <div className="min-h-screen bg-background" data-testid="settings-page">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="font-serif text-2xl sm:text-3xl font-bold">
                        {language === 'zh' ? '個人設定' : 'Personal Settings'}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {language === 'zh' ? '管理您的帳戶和偏好設定' : 'Manage your account and preferences'}
                    </p>
                </div>

                {/* Account Info Card */}
                <Card className="mb-6" data-testid="account-info-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            {language === 'zh' ? '帳戶資訊' : 'Account Information'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex items-center justify-between py-2 border-b">
                            <div className="flex items-center gap-2">
                                <Mail className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Email</span>
                            </div>
                            <span className="font-medium">{profile?.email || user?.email || 'Not set'}</span>
                        </div>
                        
                        <div className="flex items-center justify-between py-2 border-b">
                            <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Name</span>
                            </div>
                            <span className="font-medium">{profile?.name || user?.name || 'Not set'}</span>
                        </div>

                        <div className="flex items-center justify-between py-2 border-b">
                            <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-muted-foreground" />
                                <span className="text-sm text-muted-foreground">Account Type</span>
                            </div>
                            <div className="flex items-center gap-2">
                                {hasProAccess ? (
                                    <>
                                        <Badge className="bg-emerald-100 text-emerald-800">
                                            Paid
                                        </Badge>
                                        <Badge className="bg-amber-100 text-amber-800">
                                            <Crown className="w-3 h-3 mr-1" />
                                            PRO
                                        </Badge>
                                    </>
                                ) : (
                                    <Badge variant="secondary">Free</Badge>
                                )}
                            </div>
                        </div>

                        {currentAnimal && (
                            <div className="flex items-center justify-between py-2">
                                <div className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">Zodiac Animal</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="font-chinese text-lg">{currentAnimal.chinese}</span>
                                    <span className="font-medium capitalize">{currentAnimal.label}</span>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Birth Data Card */}
                <Card className="mb-6" data-testid="birth-data-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            {language === 'zh' ? '出生資料' : 'Birth Data'}
                            {birthDataLocked ? (
                                <Badge variant="secondary" className="ml-2 text-xs">
                                    <Lock className="w-3 h-3 mr-1" />
                                    {language === 'zh' ? '已鎖定' : 'Locked'}
                                </Badge>
                            ) : birthDataSaveCount > 0 ? (
                                <Badge variant="outline" className="ml-2 text-xs text-amber-600 border-amber-300">
                                    {language === 'zh' ? `還可修改 ${2 - birthDataSaveCount} 次` : `${2 - birthDataSaveCount} change${2 - birthDataSaveCount === 1 ? '' : 's'} remaining`}
                                </Badge>
                            ) : null}
                        </CardTitle>
                        <CardDescription>
                            {birthDataLocked 
                                ? (language === 'zh' 
                                    ? '出生資料已達修改上限，無法再更改'
                                    : 'Birth data has reached the maximum number of changes')
                                : (language === 'zh' 
                                    ? '用於個人化的八字分析和日期推薦（最多可修改2次）'
                                    : 'Used for personalized BaZi analysis and date recommendations (can be changed up to 2 times)')}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="birth-year">
                                    {language === 'zh' ? '出生年份' : 'Birth Year'}
                                </Label>
                                <Input
                                    id="birth-year"
                                    type="number"
                                    placeholder="1990"
                                    value={birthYear}
                                    onChange={(e) => setBirthYear(e.target.value)}
                                    min="1920"
                                    max="2025"
                                    disabled={birthDataLocked}
                                    data-testid="birth-year-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth-month">
                                    {language === 'zh' ? '月份' : 'Month'}
                                </Label>
                                <Select value={birthMonth?.toString()} onValueChange={setBirthMonth} disabled={birthDataLocked}>
                                    <SelectTrigger data-testid="birth-month-select">
                                        <SelectValue placeholder="Month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[...Array(12)].map((_, i) => (
                                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                                                {i + 1}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth-day">
                                    {language === 'zh' ? '日期' : 'Day'}
                                </Label>
                                <Select value={birthDay?.toString()} onValueChange={setBirthDay} disabled={birthDataLocked}>
                                    <SelectTrigger data-testid="birth-day-select">
                                        <SelectValue placeholder="Day" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[...Array(31)].map((_, i) => (
                                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                                                {i + 1}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth-hour">
                                    {language === 'zh' ? '時辰' : 'Hour'}
                                </Label>
                                <Select value={birthHour?.toString()} onValueChange={setBirthHour} disabled={birthDataLocked}>
                                    <SelectTrigger data-testid="birth-hour-select">
                                        <SelectValue placeholder="Hour" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unknown">Unknown</SelectItem>
                                        {[...Array(24)].map((_, i) => (
                                            <SelectItem key={i} value={i.toString()}>
                                                {i.toString().padStart(2, '0')}:00
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth-minute">
                                    {language === 'zh' ? '分鐘' : 'Minute'}
                                </Label>
                                <Select value={birthMinute?.toString()} onValueChange={setBirthMinute} disabled={birthDataLocked}>
                                    <SelectTrigger data-testid="birth-minute-select">
                                        <SelectValue placeholder="Min" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[...Array(60)].map((_, i) => (
                                            <SelectItem key={i} value={i.toString()}>
                                                :{i.toString().padStart(2, '0')}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {!birthDataLocked && (
                            <Button 
                                onClick={handleSave} 
                                disabled={saving}
                                className="w-full sm:w-auto"
                                data-testid="save-settings-btn"
                            >
                                {saving ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Save className="w-4 h-4 mr-2" />
                                )}
                                {language === 'zh' ? '儲存變更' : 'Save Changes'}
                            </Button>
                        )}
                    </CardContent>
                </Card>

                {/* Subscription Management Card - for PRO users */}
                {hasProAccess && subscription?.has_subscription && (
                    <Card className="mb-6" data-testid="subscription-card">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5" />
                                Subscription
                            </CardTitle>
                            <CardDescription>
                                Manage your PRO subscription
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between py-2 border-b">
                                <span className="text-sm text-muted-foreground">Status</span>
                                <Badge className={subscription.subscription_status === 'trialing' ? 'bg-blue-100 text-blue-800' : subscription.subscription_status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}>
                                    {subscription.subscription_status === 'trialing' ? 'Trial' : subscription.subscription_status === 'cancelled' ? 'Cancelled' : 'Active'}
                                </Badge>
                            </div>
                            
                            {subscription.trial_ends_at && (
                                <div className="flex items-center justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">
                                        {subscription.subscription_status === 'trialing' ? 'Trial Ends' : 'Access Until'}
                                    </span>
                                    <span className="font-medium">
                                        {new Date(subscription.trial_ends_at).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                            
                            {subscription.subscription_status !== 'cancelled' && (
                                <div className="flex items-center justify-between py-2 border-b">
                                    <span className="text-sm text-muted-foreground">Next Billing</span>
                                    <span className="font-medium">$47/month</span>
                                </div>
                            )}
                            
                            {subscription.can_cancel && subscription.subscription_status !== 'cancelled' && (
                                <div className="pt-4">
                                    <Button 
                                        variant="outline" 
                                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                                        onClick={handleCancelSubscription}
                                        disabled={cancellingSubscription}
                                        data-testid="cancel-subscription-btn"
                                    >
                                        {cancellingSubscription ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <AlertTriangle className="w-4 h-4 mr-2" />
                                        )}
                                        Cancel Subscription
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-2 text-center">
                                        You'll keep PRO access until the end of your billing period
                                    </p>
                                </div>
                            )}
                            
                            {subscription.subscription_status === 'cancelled' && (
                                <div className="pt-4 text-center">
                                    <p className="text-sm text-muted-foreground mb-3">
                                        Your subscription has been cancelled. You have PRO access until {new Date(subscription.trial_ends_at).toLocaleDateString()}.
                                    </p>
                                    <Button onClick={() => navigate('/subscription')}>
                                        Resubscribe
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Upgrade CTA for non-PRO users */}
                {!hasProAccess && (
                    <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
                        <CardContent className="pt-6 text-center">
                            <Crown className="w-10 h-10 text-amber-600 mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                                {language === 'zh' ? '升級至 PRO' : 'Upgrade to PRO'}
                            </h3>
                            <p className="text-sm text-amber-600 dark:text-amber-400 mt-1 mb-4">
                                {language === 'zh' 
                                    ? '解鎖所有功能、詳細分析和未來月份的預測'
                                    : 'Unlock all features, detailed analysis, and future month forecasts'}
                            </p>
                            <Button 
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                onClick={() => navigate('/subscription')}
                            >
                                {language === 'zh' ? '升級至 PRO' : 'Upgrade to PRO'} - $7 Trial
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}

export default Settings;
